// Content lookups and human-readable text for the client (names, rules text, errors, log lines).
import { contentPack } from '@bcm/content';
import {
  ANNEX_IDS,
  indexContent,
  spellValues,
  type ActionDef,
  type Color,
  type DefId,
  type GameEvent,
  type LocationId,
  type PlayerView,
  type ResolutionDef,
  type RuleError,
  type School,
  type SourceRef,
  type SpellDef,
} from '@bcm/rules';
import type { ServerError } from '@bcm/protocol';

export const pack = contentPack;
export const C = indexContent(pack);

export const SCHOOL_OF_COLOR: Record<Color, School> = {
  red: 'conjuration',
  yellow: 'transmutation',
  blue: 'evocation',
  green: 'divination',
};
export const COLOR_OF_SCHOOL: Record<School, Color> = {
  conjuration: 'red',
  transmutation: 'yellow',
  evocation: 'blue',
  divination: 'green',
};
export const SCHOOL_NAME: Record<School, string> = {
  conjuration: 'Conjuration',
  transmutation: 'Transmutation',
  evocation: 'Evocation',
  divination: 'Divination',
};
export const COLOR_NAME: Record<Color, string> = { red: 'Red', yellow: 'Yellow', blue: 'Blue', green: 'Green' };

/** Player seal colours (docs/04 §6.2): distinct from school colours, always paired with an emblem. */
export const SEAL_COLORS = ['#7b52c9', '#d9559b', '#efe6d2', '#3b3a47', '#b8693a'] as const;
export const SEAL_NAMES = ['Violet', 'Rose', 'Ivory', 'Ink', 'Copper'] as const;
/** Each seal's contrast trim on the Assistant (hat band, belt, feather, emblem): cream, or ink on Ivory. */
export const SEAL_TRIMS = ['#f1e7cf', '#f1e7cf', '#2e2433', '#f1e7cf', '#f1e7cf'] as const;
/** Seat emblems (icon names from game/icons.ts): shape + seal colour identify a player. */
export const EMBLEMS = ['quill', 'key', 'star', 'scroll', 'lantern'] as const;
export const EMBLEM_NAMES = ['Quill', 'Key', 'Seal', 'Scroll', 'Lantern'] as const;

export const isSpell = (def: DefId) => C.spells.has(def);
export const spell = (def: DefId): SpellDef => C.spells.get(def)!;
export const action = (def: DefId): ActionDef => C.actions.get(def)!;
export const defName = (def: DefId | null | undefined): string => {
  if (!def) return 'a card';
  const d =
    C.spells.get(def) ?? C.actions.get(def) ?? C.problems.get(def) ?? C.identities.get(def) ?? C.agendas.get(def) ?? C.decrees.get(def) ?? C.resolutions.get(def);
  return d?.name ?? def;
};
export const locationName = (id: LocationId) => C.locations.get(id)!.name;

// ─── Resolutions (P-17) and Annex rooms (P-18) ──────────────────────────────

export const resolution = (def: DefId): ResolutionDef | undefined => C.resolutions.get(def);
export const isAnnexRoom = (id: LocationId): boolean => (ANNEX_IDS as readonly string[]).includes(id);
/** The Annex room a Resolution opens, if it is an opener. */
export const opensRoom = (def: DefId): LocationId | null => {
  const e = C.resolutions.get(def)?.onEnact.find((x) => x.op === 'openAnnex');
  return e && e.op === 'openAnnex' ? e.location : null;
};
/** The Resolution a City Problem carries in this game (absent: none, or the option is off). */
export const problemResolution = (view: PlayerView | null | undefined, problem: DefId): DefId | undefined =>
  view?.problems.find((p) => p.problem === problem)?.resolution;
export const resolutionInForce = (view: PlayerView | null | undefined, def: DefId): boolean =>
  !!view?.resolutions?.inForce.some((r) => r.resolution === def);
/** The room a Resolution changes (Storm Power → Evocation…), for its beats and marks. */
export function resolutionRoom(def: DefId): LocationId | null {
  for (const m of C.resolutions.get(def)?.modifiers ?? []) if (m.type === 'locationBonus' || m.type === 'locationAfter') return m.location;
  return opensRoom(def);
}

/** The last round of this game (Early Closing brings it forward). */
export const lastRound = (view: PlayerView): number => view.lastRound ?? view.options.rounds;
/** Is a location on the board right now (Annex rooms only while open in a bay)? */
export function roomOpen(view: PlayerView, id: LocationId): boolean {
  if (id === 'precedence') return view.options.precedenceOffice;
  if (isAnnexRoom(id)) return !!view.annex?.bays.includes(id);
  return true;
}
/** A location's capacity: its own fixed one (the Office of Precedence holds 1), else the table's. */
export const locationCapacity = (view: PlayerView, id: LocationId): number =>
  C.locations.get(id)?.capacity ?? view.capacity ?? view.options.capacity[String(view.playerCount)] ?? 2;
export const shortLocation = (id: LocationId) => locationName(id).replace(/^(Dept\. of |The )/, '');

export function values(def: DefId, view: PlayerView | null) {
  return spellValues(C, def, view?.options ?? pack.defaults);
}

export function sourceName(src: SourceRef): string {
  switch (src.kind) {
    case 'card':
      return defName(src.def);
    case 'identity':
      return defName(src.def);
    case 'location':
      return locationName(src.id);
    case 'problem':
      return defName(src.def);
    case 'agenda':
      return defName(src.def);
    case 'decree':
      return defName(src.def);
    case 'resolution':
      return defName(src.def);
    default:
      return 'the Ministry';
  }
}

export function roundHour(round: number): string {
  const h = 8 + Math.max(1, round);
  return h <= 12 ? `${h}:00 AM`.replace('12:00 AM', '12:00 PM') : `${h - 12}:00 PM`;
}

/** Closing time: 5 PM after round 8, 4 PM after Early Closing. */
export const closingHour = (last: number): string => roundHour(last + 1);

// ─── Errors ("Why not?") ─────────────────────────────────────────────────────

export function errorText(e: RuleError | ServerError | undefined | null): string {
  if (!e) return '';
  const p = e.params ?? {};
  switch (e.code) {
    case 'LOCATION_FULL':
      return `${shortLocation(p.location as LocationId)} is full (${p.capacity}/${p.capacity})`;
    case 'LOCATION_CLOSED':
      return `${shortLocation(p.location as LocationId)} is closed in this ruleset`;
    case 'LOCATION_BLOCKED':
      return `${shortLocation(p.location as LocationId)} is under Audit this round`;
    case 'INSUFFICIENT_PERMITS':
      return `Needs ${p.missing} more ${COLOR_NAME[p.color as Color]} permit${Number(p.missing) > 1 ? 's' : ''}`;
    case 'INSUFFICIENT_MANA':
      return `Needs ${p.missing} more mana`;
    case 'NOT_YOUR_TURN':
      return 'Not your turn';
    case 'ALREADY_PLACED':
      return 'You already placed an Assistant this turn';
    case 'NO_ASSISTANT_AVAILABLE':
      return 'No Assistants left this round';
    case 'MUST_PLACE':
      return 'Place an Assistant before ending your turn';
    case 'DECISION_PENDING':
      return 'Finish the current decision first';
    case 'ABILITY_USED':
      return 'Already used';
    case 'ABILITY_COST':
      return p.mana ? `Costs ${p.mana} mana` : 'Needs an Action card to discard';
    case 'NOTHING_TO_UNDO':
      return 'Nothing to undo (information was revealed)';
    case 'GAME_OVER':
      return 'The game is over';
    case 'NOT_HOST':
      return 'Only the host can do that';
    case 'ROOM_FULL':
      return 'The room is full';
    case 'NOT_SEATED':
      return 'You are spectating';
    case 'VERSION':
      return 'Your client is out of date — reload the page';
    case 'RATE_LIMIT':
      return 'Slow down a little';
    case 'WRONG_STATUS':
      return 'Not possible right now';
    case 'INVALID_PAYMENT':
      return 'That payment is not valid';
    case 'HOLDS_STAMP':
      return 'You already hold the Stamp of Precedence';
    case 'NOT_IN_FORCE':
      return 'Only while Memos Fly Free is in force';
    case 'FREE_ACTION_USED':
      return 'You already sent a free memo this round';
    default:
      return e.code.replace(/_/g, ' ').toLowerCase();
  }
}

// ─── Log lines ───────────────────────────────────────────────────────────────

const bagText = (bag: Partial<Record<Color, number>>) =>
  (Object.entries(bag) as [Color, number][])
    .filter(([, n]) => n > 0)
    .map(([c, n]) => `{${c[0]!.toUpperCase()}}`.repeat(n))
    .join('');

/**
 * The cards an event names, in the order its log line names them (so the Ledger can make each name
 * inspectable). Hidden cards (a rival's draw) carry no definition and are left out.
 */
export function logDefs(e: GameEvent): string[] {
  const out: string[] = [];
  const add = (d: unknown) => {
    if (typeof d === 'string' && d.includes('.') && !out.includes(d) && (C.spells.has(d) || C.actions.has(d) || C.problems.has(d) || C.decrees.has(d) || C.identities.has(d) || C.resolutions.has(d))) out.push(d);
  };
  const r = e as Record<string, unknown>;
  for (const k of ['def', 'handDef', 'arcDef', 'decree', 'superseded', 'problem', 'identity', 'resolution']) add(r[k]);
  const src = r.source as { def?: string } | undefined;
  add(src?.def);
  return out;
}

/** A short log line for an event, or null to skip. Uses glyph tokens ({R}, {MANA}, …). */
export function logLine(e: GameEvent, name: (seat: number) => string): string | null {
  switch (e.t) {
    case 'RoundStarted':
      return `— Round ${e.round} · ${roundHour(e.round)} —`;
    case 'AssistantPlaced':
      return `${name(e.seat)} → ${shortLocation(e.location)}`;
    case 'AssistantSentHome':
      return e.skipped ? `${name(e.seat)} skipped their turn` : `${name(e.seat)} had nowhere to go (+1 {MANA})`;
    case 'PermitsGained':
      return `${name(e.seat)} +${bagText(e.permits)}`;
    case 'ManaGained':
      return e.source.kind === 'rules' ? null : `${name(e.seat)} +${e.amount} {MANA}`;
    case 'SpellPurchased':
      return `${name(e.seat)} bought ${defName(e.def)} (${e.price} {MANA})`;
    case 'SpellCast':
      return `${name(e.seat)} cast ${defName(e.def)}`;
    case 'ActionCardPlayed':
      return e.free ? `${name(e.seat)} sent ${defName(e.def)} as a free memo` : `${name(e.seat)} played ${defName(e.def)}`;
    case 'ProblemClaimed':
      return `${name(e.seat)} resolved ${defName(e.problem)} (+${e.vp} {VP})`;
    case 'LocationBlocked':
      return `${name(e.seat)} audited ${shortLocation(e.location)}`;
    case 'PermitsExchanged':
      return `${name(e.seat)} swapped {${e.gave[0]!.toUpperCase()}} for {${e.took[0]!.toUpperCase()}} with ${name(e.target)}`;
    case 'AbilityTriggered':
      return `${sourceName(e.source)} triggered`;
    case 'AbilityUsed':
      return `${name(e.seat)} used ${sourceName(e.source)}`;
    case 'AssistantGained':
      return e.intern ? `${name(e.seat)} takes on an Intern` : `${name(e.seat)} gained ${e.temp ? 'a temporary' : 'an'} Assistant`;
    case 'InternsHired':
      return `— The Afternoon Shift: everyone takes on an Intern —`;
    case 'StampTaken':
      return `${name(e.seat)} took the Stamp of Precedence from ${name(e.from)}`;
    case 'FirstPlayerPassed':
      return `${name(e.seat)} goes first`;
    case 'RankPromoted':
      return `${name(e.seat)} promoted to ${e.title}`;
    case 'SpellSwapped':
      return `${name(e.seat)} swapped ${defName(e.handDef)} for ${defName(e.arcDef)}`;
    case 'DecreeEnacted':
      return `${name(e.seat)} enacted ${defName(e.decree)}${e.superseded ? ` (superseding ${defName(e.superseded)})` : ''}`;
    case 'DecreeRepealed':
      return e.resolution ? `${defName(e.resolution)} struck ${defName(e.decree)}` : `${name(e.seat)} repealed ${defName(e.decree)}`;
    case 'DecreeFizzled':
      return `${defName(e.decree)} can't be enacted (${defName(e.resolution)})`;
    case 'ResolutionQueued':
      return `${defName(e.resolution)} takes effect next round`;
    case 'ResolutionEnacted':
      return `— Resolution: ${defName(e.resolution)} —`;
    case 'CoinFlipped':
      return `${name(e.seat)} flipped ${e.result} at ${shortLocation(e.location)}${e.result === 'heads' ? ': again!' : ''}`;
    case 'AnnexOpened':
      return e.replaced ? `${locationName(e.location)} opens in place of ${locationName(e.replaced)}` : `${locationName(e.location)} opens in the Annex`;
    case 'HandsRevealed':
      return '— The Fog Lifts: every hand is face up —';
    case 'ActionCardGiven':
      return `${name(e.seat)} passed ${e.def ? defName(e.def) : 'an Action card'} to ${name(e.to)}`;
    case 'SpellRecovered':
      return `${name(e.seat)} recovered ${defName(e.def)} from Lost Property`;
    case 'StartingPackage':
      return `${name(e.seat)} opens their starting package`;
    case 'DecreeIssued':
      return `The Ministry issued ${defName(e.decree)}${e.superseded ? ` (superseding ${defName(e.superseded)})` : ''}`;
    case 'ArcaniumRefreshed':
      return `${name(e.seat)} refreshed the Arcanium (−${e.mana}{MANA})`;
    case 'AssistantCapped':
      return `${name(e.seat)} already has ${e.cap} Assistants (the most allowed)`;
    case 'SpellSuspended':
      return `${name(e.seat)} put ${name(e.owner)}'s ${defName(e.def)} Under Review`;
    case 'SpellReinstated':
      return `${name(e.owner)}'s ${defName(e.def)} is reinstated`;
    case 'SpellRevoked':
      return `${name(e.seat)} revoked ${name(e.owner)}'s ${defName(e.def)}`;
    case 'ManaPaid':
      return `${name(e.seat)} paid ${name(e.to)} ${e.amount} {MANA} (${e.reason})`;
    case 'ActionStolen':
      return `${name(e.seat)} subpoenaed ${e.def ? defName(e.def) : 'an Action card'} from ${name(e.from)}`;
    case 'VPGained':
      return e.amount < 0 ? `${name(e.seat)} ${e.amount} {VP}` : null;
    case 'DraftResolved':
      return 'Identities revealed';
    case 'TurnRewound':
      return `${name(e.seat)} undid their last action`;
    case 'GameEnded':
      return 'Closing time — the Ledger is tallied';
    default:
      return null;
  }
}
